import React, { useMemo } from 'react';
import { EVALUATION_MODE_LABELS, type EvaluationMode } from '../../types/evaluation';
import EvaluationEvidenceList from './EvaluationEvidenceList';
import UsageBreakdown from '../UsageBreakdown';
import './EvaluationRunResultView.css';

function unwrapResultPayload(data: unknown): Record<string, unknown> | null {
  if (data == null || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  if (typeof obj.data === 'object' && obj.data !== null && !Array.isArray(obj.data)) {
    return obj.data as Record<string, unknown>;
  }
  return obj;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function formatReason(reason: string): string {
  return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatEvaluatorKind(kind: string): string {
  if (kind in EVALUATION_MODE_LABELS) {
    return EVALUATION_MODE_LABELS[kind as EvaluationMode];
  }
  return formatReason(kind);
}

function formatScore(score: unknown): string {
  if (score == null) return '—';
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(4);
}



function StatCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  accent?: 'success' | 'danger' | 'neutral';
}) {
  return (
    <div className={`eval-run-result__stat eval-run-result__stat--${accent ?? 'neutral'}`}>
      <div className="eval-run-result__stat-label">{label}</div>
      <div className="eval-run-result__stat-value">{value}</div>
      {hint ? <div className="eval-run-result__stat-hint">{hint}</div> : null}
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="eval-run-result__row">
      <dt className="eval-run-result__row-label">{label}</dt>
      <dd className="eval-run-result__row-value">{children}</dd>
    </div>
  );
}

export interface EvaluationRunResultViewProps {
  data: unknown;
}

const EvaluationRunResultView: React.FC<EvaluationRunResultViewProps> = ({ data }) => {
  const payload = useMemo(() => unwrapResultPayload(data), [data]);

  const derived = useMemo(() => {
    if (!payload) return null;

    const evaluation = asRecord(payload.evaluation);
    const evidence = evaluation?.evidence;
    const evidenceList = Array.isArray(evidence) ? evidence : [];
    const perItemScores = evaluation ? asRecord(evaluation.per_item_scores) : null;
    const perItemEntries = perItemScores
      ? Object.entries(perItemScores).filter(([, v]) => v != null)
      : [];

    return {
      status: typeof payload.status === 'string' ? payload.status : 'unknown',
      reason: typeof payload.reason === 'string' ? payload.reason : '',
      errorText: typeof payload.error === 'string' ? payload.error.trim() : '',
      dataset: asRecord(payload.dataset),
      evaluator: asRecord(payload.evaluator),
      usage: asRecord(payload.usage),
      evidenceList,
      perItemEntries,
      metricId: typeof evaluation?.metric_id === 'string' ? evaluation.metric_id : '—',
      score: evaluation?.score,
    };
  }, [payload]);

  if (!payload || !derived) {
    return <p className="eval-run-result__empty">No result data</p>;
  }

  const {
    status,
    reason,
    errorText,
    dataset,
    evaluator,
    usage,
    evidenceList,
    perItemEntries,
    metricId,
    score,
  } = derived;

  const statusLower = status.toLowerCase();
  const isFailed = statusLower === 'failed' || statusLower === 'error';
  const isSuccess = statusLower === 'completed' || statusLower === 'success' || statusLower === 'succeeded';

  const evaluatorParams = evaluator ? asRecord(evaluator.params) : null;
  const evaluatorKind = typeof evaluator?.kind === 'string' ? evaluator.kind : '';

  const datasetNames = Array.isArray(dataset?.names)
    ? dataset!.names.filter((n): n is string => typeof n === 'string')
    : [];

  return (
    <section className="eval-run-result">
      <div
        className={`eval-run-result__banner eval-run-result__banner--${
          isFailed ? 'failed' : isSuccess ? 'success' : 'neutral'
        }`}
      >
        <div className="eval-run-result__banner-icon" aria-hidden>
          {isFailed ? (
            <i className="fas fa-circle-xmark" />
          ) : isSuccess ? (
            <i className="fas fa-circle-check" />
          ) : (
            <i className="fas fa-circle-info" />
          )}
        </div>
        <div className="eval-run-result__banner-body">
          <div className="eval-run-result__banner-top">
            <h4 className="eval-run-result__banner-title">
              {isFailed ? 'Evaluation failed' : isSuccess ? 'Evaluation completed' : 'Evaluation result'}
            </h4>
            <span className={`eval-run-result__status-pill eval-run-result__status-pill--${statusLower}`}>
              {status}
            </span>
          </div>
          {reason ? (
            <p className="eval-run-result__banner-reason">{formatReason(reason)}</p>
          ) : null}
          {errorText ? (
            <p className="eval-run-result__banner-error">{errorText}</p>
          ) : isFailed && !errorText ? (
            <p className="eval-run-result__banner-error eval-run-result__banner-error--muted">
              No score was produced. Check dataset items, evaluator settings, and agent config.
            </p>
          ) : null}
        </div>
      </div>

      <div className="eval-run-result__stats">
        <StatCard
          label="Score"
          value={formatScore(score)}
          hint={score == null ? 'Not computed' : undefined}
          accent={score != null ? (isFailed ? 'neutral' : 'success') : isFailed ? 'danger' : 'neutral'}
        />
        <StatCard label="Metric" value={metricId} />
        <StatCard
          label="Evaluated"
          value={
            dataset?.evaluated_items != null
              ? `${dataset.evaluated_items}${dataset.batch_size != null ? ` / ${dataset.batch_size}` : ''}`
              : '—'
          }
          hint={dataset?.split != null ? String(dataset.split) : undefined}
        />
        <StatCard
          label="Evaluator"
          value={evaluatorKind ? formatEvaluatorKind(evaluatorKind) : '—'}
          hint={
            evaluatorParams?.embedding_model_name
              ? String(evaluatorParams.embedding_model_name)
              : evaluatorParams?.model_name
                ? String(evaluatorParams.model_name)
                : undefined
          }
        />
      </div>

      {usage && (
        <div className="eval-run-result__panel">
          <UsageBreakdown usage={usage} />
        </div>
      )}

      {dataset && (
        <div className="eval-run-result__panel">
          <h5 className="eval-run-result__panel-title">
            <i className="fas fa-database" aria-hidden /> Dataset
          </h5>
          <dl className="eval-run-result__rows">
            {dataset.source != null ? (
              <DetailRow label="Source">{String(dataset.source)}</DetailRow>
            ) : null}
            {dataset.split != null ? (
              <DetailRow label="Split">{String(dataset.split)}</DetailRow>
            ) : null}
            {dataset.batch_size != null ? (
              <DetailRow label="Batch size">{String(dataset.batch_size)}</DetailRow>
            ) : null}
            {datasetNames.length > 0 ? (
              <DetailRow label="Dataset ID">
                {datasetNames.length === 1 ? (
                  <code className="eval-run-result__code">{datasetNames[0]}</code>
                ) : (
                  <details className="eval-run-result__dataset-ids">
                    <summary className="eval-run-result__dataset-ids-summary">
                      <code className="eval-run-result__code">{datasetNames[0]}</code>
                      <span className="eval-run-result__dataset-more">
                        +{datasetNames.length - 1} more
                      </span>
                    </summary>
                    <ul className="eval-run-result__dataset-list">
                      {datasetNames.map((name, idx) => (
                        <li key={`${name}-${idx}`}>
                          <code className="eval-run-result__code">{name}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </DetailRow>
            ) : null}
          </dl>
        </div>
      )}

      {evaluator && (
        <div className="eval-run-result__panel">
          <h5 className="eval-run-result__panel-title">
            <i className="fas fa-sliders" aria-hidden /> Evaluator
          </h5>
          <dl className="eval-run-result__rows">
            {evaluatorKind ? (
              <DetailRow label="Mode">{formatEvaluatorKind(evaluatorKind)}</DetailRow>
            ) : null}
            {evaluatorParams
              ? Object.entries(evaluatorParams).map(([key, value]) => (
                  <DetailRow key={key} label={formatReason(key)}>
                    {typeof value === 'object' && value !== null ? (
                      <pre className="eval-run-result__inline-json">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : (
                      String(value ?? '—')
                    )}
                  </DetailRow>
                ))
              : null}
          </dl>
        </div>
      )}

      {perItemEntries.length > 0 && (
        <div className="eval-run-result__panel">
          <h5 className="eval-run-result__panel-title">
            <i className="fas fa-list-ol" aria-hidden /> Per-item scores
          </h5>
          <div className="eval-run-result__table-wrap">
            <table className="eval-run-result__table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {perItemEntries.map(([itemId, itemScore]) => (
                  <tr key={itemId}>
                    <td>
                      <code className="eval-run-result__code">{itemId}</code>
                    </td>
                    <td>{formatScore(itemScore)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {evidenceList.length > 0 && (
        <div className="eval-run-result__panel">
          <h5 className="eval-run-result__panel-title">
            <i className="fas fa-microscope" aria-hidden /> Evidence ({evidenceList.length})
          </h5>
          <EvaluationEvidenceList items={evidenceList} />
        </div>
      )}
    </section>
  );
};

export default React.memo(EvaluationRunResultView);
